import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Lock, Crown, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useWalkthrough } from '../components/OnboardingWalkthrough';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountDetails = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const { startWalkthrough } = useWalkthrough();

  // Redirect free users (those without any active tier or course access)
  const hasPaidMembership = user?.membership_tier && ['starter', 'pro', 'elite'].includes(user.membership_tier);
  const hasLifetimeAccess = user?.has_course_access || user?.membership_status === 'lifetime';
  
  if (!user || (!hasPaidMembership && !hasLifetimeAccess)) {
    return (
      <div className="min-h-screen py-24" data-testid="account-details-page">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="glass-panel rounded-2xl p-8 border text-center">
            <AlertTriangle size={48} className="mx-auto mb-4" style={{ color: '#ef4444' }} />
            <h2 className="text-2xl font-bold mb-4">Account Access Restricted</h2>
            <p className="text-muted-foreground mb-6">
              Account details are only available to users with an active membership (Starter, Pro, or Elite).
            </p>
            <Button onClick={() => navigate('/pricing')} style={{ background: '#D4AF37', color: '#141414' }}>
              View Membership Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const getTierDisplay = () => {
    const tiers = {
      starter: { name: 'Starter', color: '#D4AF37' },
      pro: { name: 'Pro', color: '#D4AF37' },
      elite: { name: 'Elite', color: '#D4AF37' }
    };
    return tiers[user.membership_tier] || { name: 'Free', color: '#808080' };
  };

  const tierInfo = getTierDisplay();
  const isLifetime = user.has_course_access && (!user.stripe_subscription_id || user.membership_status === 'lifetime');
  // Show manage button for any active subscription (Starter, Pro, Elite)
  const hasActiveSubscription = user.membership_status === 'active' && ['starter', 'pro', 'elite'].includes(user.membership_tier);
  const canCancel = hasActiveSubscription;

  const handleCancelMembership = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/membership/cancel`);
      setCancelled(true);
      setShowCancelModal(false);
      toast.success('Membership cancelled successfully');
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen py-24" data-testid="account-details-page">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
            <User size={40} style={{ color: '#D4AF37' }} />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#ffffff' }}>Account Details</h1>
          <p className="text-muted-foreground">Manage your account and membership</p>
        </div>

        {/* Cancellation Success Message */}
        {cancelled && (
          <div className="rounded-2xl p-6 border mb-8" style={{ background: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }} data-testid="cancellation-success">
            <div className="flex items-start gap-4">
              <CheckCircle size={24} style={{ color: '#22c55e' }} className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#22c55e' }}>Membership Cancelled</h3>
                <p style={{ color: '#86efac' }}>
                  Your membership has been cancelled. Please note: if your cancellation occurs within 2 days of a new billing cycle, funds may still leave your account. To avoid further charges, please notify your bank or ensure your direct debit has been ended.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Account Details Card */}
        <div className="glass-panel rounded-2xl border overflow-hidden">
          {/* Email */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <Mail size={20} style={{ color: '#D4AF37' }} />
              </div>
              <div className="flex-1">
                <label className="text-sm text-muted-foreground">Email Address</label>
                <p className="text-lg font-medium" style={{ color: '#ffffff' }} data-testid="account-email">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <Lock size={20} style={{ color: '#D4AF37' }} />
              </div>
              <div className="flex-1">
                <label className="text-sm text-muted-foreground">Password</label>
                <p className="text-lg font-medium" style={{ color: '#ffffff' }} data-testid="account-password">
                  ••••••••••••
                </p>
              </div>
            </div>
          </div>

          {/* Membership Tier */}
          <div className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                  <Crown size={20} style={{ color: '#D4AF37' }} />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Current Membership Tier</label>
                  <div className="flex items-center gap-2 mt-1">
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-bold" 
                      style={{ background: tierInfo.color, color: '#141414' }}
                      data-testid="account-tier"
                    >
                      {tierInfo.name}
                    </span>
                    {isLifetime && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
                        Lifetime Access
                      </span>
                    )}
                    {user.membership_status === 'cancelling' && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
                        Cancelling
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Manage Membership Button */}
              {canCancel && (
                <Button
                  onClick={() => setShowCancelModal(true)}
                  variant="outline"
                  className="px-6"
                  style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                  data-testid="manage-membership-btn"
                >
                  Manage my membership
                </Button>
              )}

              {user.membership_status === 'cancelling' && (
                <p className="text-sm text-muted-foreground">
                  Your membership will end at the current billing period.
                </p>
              )}

              {isLifetime && !user.stripe_subscription_id && (
                <p className="text-sm text-muted-foreground">
                  Lifetime access from course purchase
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 glass-panel rounded-2xl p-6 border">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <HelpCircle size={20} style={{ color: '#D4AF37' }} />
              </div>
              <div>
                <p className="font-medium">Need a refresher?</p>
                <p className="text-sm text-muted-foreground">Replay the platform walkthrough</p>
              </div>
            </div>
            <Button
              onClick={() => {
                startWalkthrough();
                navigate('/dashboard');
                toast.success('Starting walkthrough...');
              }}
              variant="outline"
              className="px-6"
              style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
              data-testid="restart-tour-btn"
            >
              Restart Tour
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Need help? Contact us at{' '}
            <a href="mailto:contact@blendonics.com" style={{ color: '#D4AF37' }}>
              contact@blendonics.com
            </a>
          </p>
        </div>
      </div>

      {/* Cancel Membership Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }} data-testid="cancel-modal">
          <div className="glass-panel rounded-2xl border max-w-lg w-full p-6 md:p-8" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="text-center mb-6">
              <AlertTriangle size={48} className="mx-auto mb-4" style={{ color: '#ef4444' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>
                Confirm Membership Cancellation
              </h2>
            </div>

            <div className="space-y-4 mb-8">
              <p className="text-center" style={{ color: '#a3a3a3' }}>
                Are you sure you want to cancel your membership?
              </p>

              <div className="p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <p className="text-sm mb-3" style={{ color: '#fca5a5' }}>
                  Cancelling your membership will immediately stop future renewals. However, please note:
                </p>
                <ul className="text-sm space-y-2" style={{ color: '#fca5a5' }}>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>If your cancellation is made within 2 days of a new billing cycle, a payment may still be processed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>This is due to bank and direct debit processing times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Any payment already in progress cannot be reversed automatically</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <p className="text-sm" style={{ color: '#D4AF37' }}>
                  If a payment is still taken after cancellation, please contact your bank to stop or reverse the direct debit.
                </p>
              </div>

              <p className="text-sm text-center" style={{ color: '#a3a3a3' }}>
                By confirming below, you acknowledge and accept these terms. By proceeding, you confirm that you understand a charge may still occur if cancellation is within 2 days of your billing cycle.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleCancelMembership}
                disabled={cancelling}
                className="w-full py-6 text-base font-semibold"
                style={{ background: '#ef4444', color: '#ffffff' }}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? 'Ending membership...' : 'End my membership'}
              </Button>
              <Button
                onClick={() => setShowCancelModal(false)}
                className="w-full py-6 text-base font-semibold"
                style={{ background: '#22c55e', color: '#ffffff' }}
                data-testid="keep-membership-btn"
              >
                Keep my membership
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetails;
